import { useToast } from '@chakra-ui/react';
import authApi, { LoginPayload } from 'api/auth';
import { useMutation } from 'react-query';

import { LoginProps } from './Login.props';
import LoginView from './Login.view';
import { useNavigate } from 'react-router-dom';
import { AccountTypes } from 'types/accountTypes';
import { useAppDispatch } from 'store/hooks';
import { AuthActions } from 'store';
import { Permissions, RolesEnum } from 'types/user';

const LoginContainer = (_: LoginProps): JSX.Element => {
  const toast = useToast();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { mutate: login, isLoading } = useMutation(authApi.login, {
    onError: (e: any) => {
      const errorMessage =
        e?.response?.data?.error?.message || 'Invalid email or password';

      toast({
        status: 'error',
        title: 'Login failed',
        description: errorMessage,
        duration: 10000,
        isClosable: true,
        position: 'top',
      });
    },
    onSuccess: (response) => {
      if (response?.user?.accountType === AccountTypes.admin) {
        dispatch(AuthActions.saveUserInfo(response));
        navigate('/dashboard');
      }
    },
  });

  const { mutate: googleAuth, isLoading: isGoogleAuthLoading } = useMutation(
    authApi.googleAuth,
    {
      onError: (e: any) => {
        const errorMessage =
          e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          'Invalid email or password';

        toast({
          status: 'error',
          title: 'Login failed',
          description: errorMessage,
          duration: 10000,
          isClosable: true,
          position: 'top',
        });
      },
      onSuccess: (response: any) => {
        const permissions: Permissions = response?.item?.permissions;
        const isWashHubStaff = permissions
          .map((p) => p.role)
          .includes(RolesEnum.WASH_HUB_STAFF);

        if (isWashHubStaff) {
          toast({
            status: 'error',
            title: 'Unauthorised',
            description: 'Wash Staff cannot login to Admin dashboard',
            isClosable: true,
            duration: 3000,
          });

          return;
        }

        dispatch(AuthActions.saveUserInfo(response?.item));
        navigate('/dashboard');
      },
    },
  );

  const onSignupPress = () => {
    navigate('/register');
  };

  const onLogin = async (payload: LoginPayload) => {
    try {
      await login(payload);
    } catch (e) {
      console.log('Login request error');
    }
  };

  const onCredentialResponse = ({ credential }: { credential: string }) =>
    googleAuth(credential);

  return (
    <>
      <LoginView
        onSubmit={onLogin}
        isLoading={isLoading}
        isGoogleAuthLoading={isGoogleAuthLoading}
        onSignupPress={onSignupPress}
        onCredentialResponse={onCredentialResponse}
      />
    </>
  );
};

export default LoginContainer;
